/**
 * A button's last 20 seconds, as a step line in a table cell.
 *
 * Hand-rolled SVG on purpose. `charts/guages/Sparkline.tsx` renders a whole gauge card (label, 30px value
 * text, its own background and ResizeObserver) and reads playbackStore itself; `LiveMapUPlotTimeSeries` is a
 * full timeline with axes and scrub wiring. One of either per row, times however many buttons a boat has, is
 * far more machinery than a 20-point trace needs — and neither draws a square wave.
 *
 * STEP, NOT SLOPE. A button is on or off; a diagonal between samples would draw a transition that never
 * happened. The path therefore holds each value until the next sample and turns at right angles.
 *
 * `NaN` breaks the line rather than plotting as zero, so a gap in the feed is visibly a gap and not a release.
 */

import { createMemo, For, Show } from 'solid-js';

export interface ButtonSparklineProps {
  /** Sample times, epoch ms, ascending. */
  ts: number[];
  /** Values aligned to `ts`; NaN means no sample. */
  values: number[];
  /** Right edge of the window (the cursor), epoch ms. */
  cursorMs: number;
  /** Window width in ms. The left edge is `cursorMs - windowMs`. */
  windowMs: number;
  width?: number;
  height?: number;
}

const DEFAULT_WIDTH = 96;
const DEFAULT_HEIGHT = 18;
/** Keeps the 1px stroke off the cell edge at both rails. */
const PAD_Y = 2;

export default function ButtonSparkline(props: ButtonSparklineProps) {
  const width = () => props.width ?? DEFAULT_WIDTH;
  const height = () => props.height ?? DEFAULT_HEIGHT;

  /**
   * Contiguous runs of real samples, as SVG path data.
   *
   * y is a two-level rail rather than a scaled axis: anything above zero sits on the top rail. The point of
   * the trace is WHEN the button was pressed, not how far — a value of 2 is not "twice as pressed", and
   * scaling to the window's max would make an all-zero window's noise look like activity.
   */
  const paths = createMemo(() => {
    const ts = props.ts;
    const values = props.values;
    const w = width();
    const h = height();
    const windowMs = props.windowMs;
    const endMs = props.cursorMs;
    const startMs = endMs - windowMs;
    if (!Number.isFinite(endMs) || windowMs <= 0 || ts.length === 0) {return [] as string[];}

    const x = (ms: number) => ((ms - startMs) / windowMs) * w;
    const yOn = PAD_Y;
    const yOff = h - PAD_Y;
    const y = (v: number) => (v > 0 ? yOn : yOff);

    const out: string[] = [];
    let current: string[] = [];
    let prevY: number | null = null;

    const flush = () => {
      // A single sample has no width to draw, but it still tells you the button reported - give it a tick.
      if (current.length === 1) {current.push(`h 2`);}
      if (current.length > 1) {out.push(current.join(' '));}
      current = [];
      prevY = null;
    };

    for (let i = 0; i < ts.length; i += 1) {
      const v = values[i];
      if (!Number.isFinite(v)) {
        flush();
        continue;
      }
      const px = x(ts[i]);
      const py = y(v);
      if (prevY === null) {
        current.push(`M ${px.toFixed(1)} ${py.toFixed(1)}`);
      } else {
        // Horizontal to the new sample's time at the OLD level, then vertical to the new level: the value
        // held until this instant, then changed.
        current.push(`L ${px.toFixed(1)} ${prevY.toFixed(1)}`);
        if (py !== prevY) {current.push(`L ${px.toFixed(1)} ${py.toFixed(1)}`);}
      }
      prevY = py;
    }

    // Hold the last known level out to the cursor, so the trace reaches the right edge like the LED implies.
    if (prevY !== null) {current.push(`L ${w.toFixed(1)} ${prevY.toFixed(1)}`);}
    flush();
    return out;
  });

  return (
    <Show
      when={paths().length > 0}
      fallback={<span class="buttons-page__sparkline-empty" aria-label="No samples">—</span>}
    >
      <svg
        class="buttons-page__sparkline"
        width={width()}
        height={height()}
        viewBox={`0 0 ${width()} ${height()}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <For each={paths()}>
          {(d) => <path d={d} fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="miter" />}
        </For>
      </svg>
    </Show>
  );
}
